import { ISO2_TO_ISO3 } from './countryCodes';

const toIso3 = (iso2: string, fallbackName: string): string => {
  const normalized = (iso2 || '').trim().toUpperCase();
  if (normalized.length === 2 && ISO2_TO_ISO3[normalized]) {
    return ISO2_TO_ISO3[normalized];
  }

  const letters = (fallbackName || '').replace(/[^a-z]/gi, '').slice(0, 3);
  if (letters.length === 3) {
    return letters.toUpperCase();
  }

  return `${normalized || 'XX'}X`.slice(0, 3).toUpperCase();
};

export type GeoRegion =
  | 'global'
  | 'africa'
  | 'asia'
  | 'europe'
  | 'north-america'
  | 'south-america'
  | 'oceania'
  | 'middle-east';

export interface CountryOption {
  code: string;
  iso3: string;
  name: string;
  region: GeoRegion;
}

export const REGION_LABELS: Record<GeoRegion, string> = {
  'global': 'Global',
  'africa': 'Africa',
  'asia': 'Asia',
  'europe': 'Europe',
  'north-america': 'North America',
  'south-america': 'South America',
  'oceania': 'Oceania',
  'middle-east': 'Middle East'
};

const countriesData = [
  { name: 'Algeria', code: 'DZ', region: 'africa' },
  { name: 'Angola', code: 'AO', region: 'africa' },
  { name: 'Argentina', code: 'AR', region: 'south-america' },
  { name: 'Australia', code: 'AU', region: 'oceania' },
  { name: 'Austria', code: 'AT', region: 'europe' },
  { name: 'Bahrain', code: 'BH', region: 'middle-east' },
  { name: 'Bangladesh', code: 'BD', region: 'asia' },
  { name: 'Belgium', code: 'BE', region: 'europe' },
  { name: 'Bolivia', code: 'BO', region: 'south-america' },
  { name: 'Botswana', code: 'BW', region: 'africa' },
  { name: 'Brazil', code: 'BR', region: 'south-america' },
  { name: 'Canada', code: 'CA', region: 'north-america' },
  { name: 'Chile', code: 'CL', region: 'south-america' },
  { name: 'China', code: 'CN', region: 'asia' },
  { name: "Côte d'Ivoire", code: 'CI', region: 'africa' },
  { name: 'Colombia', code: 'CO', region: 'south-america' },
  { name: 'Costa Rica', code: 'CR', region: 'north-america' },
  { name: 'Croatia', code: 'HR', region: 'europe' },
  { name: 'Czech Republic', code: 'CZ', region: 'europe' },
  { name: 'Denmark', code: 'DK', region: 'europe' },
  { name: 'Dominican Republic', code: 'DO', region: 'north-america' },
  { name: 'Egypt', code: 'EG', region: 'africa' },
  { name: 'El Salvador', code: 'SV', region: 'north-america' },
  { name: 'Estonia', code: 'EE', region: 'europe' },
  { name: 'Ethiopia', code: 'ET', region: 'africa' },
  { name: 'Fiji', code: 'FJ', region: 'oceania' },
  { name: 'Finland', code: 'FI', region: 'europe' },
  { name: 'France', code: 'FR', region: 'europe' },
  { name: 'Germany', code: 'DE', region: 'europe' },
  { name: 'Ghana', code: 'GH', region: 'africa' },
  { name: 'Greece', code: 'GR', region: 'europe' },
  { name: 'Guatemala', code: 'GT', region: 'north-america' },
  { name: 'Hong Kong', code: 'HK', region: 'asia' },
  { name: 'Hungary', code: 'HU', region: 'europe' },
  { name: 'Iceland', code: 'IS', region: 'europe' },
  { name: 'India', code: 'IN', region: 'asia' },
  { name: 'Indonesia', code: 'ID', region: 'asia' },
  { name: 'Iran', code: 'IR', region: 'middle-east' },
  { name: 'Iraq', code: 'IQ', region: 'middle-east' },
  { name: 'Ireland', code: 'IE', region: 'europe' },
  { name: 'Israel', code: 'IL', region: 'middle-east' },
  { name: 'Italy', code: 'IT', region: 'europe' },
  { name: 'Jamaica', code: 'JM', region: 'north-america' },
  { name: 'Japan', code: 'JP', region: 'asia' },
  { name: 'Jordan', code: 'JO', region: 'middle-east' },
  { name: 'Kenya', code: 'KE', region: 'africa' },
  { name: 'Kuwait', code: 'KW', region: 'middle-east' },
  { name: 'Lebanon', code: 'LB', region: 'middle-east' },
  { name: 'Libya', code: 'LY', region: 'africa' },
  { name: 'Lithuania', code: 'LT', region: 'europe' },
  { name: 'Luxembourg', code: 'LU', region: 'europe' },
  { name: 'Madagascar', code: 'MG', region: 'africa' },
  { name: 'Malaysia', code: 'MY', region: 'asia' },
  { name: 'Mexico', code: 'MX', region: 'north-america' },
  { name: 'Morocco', code: 'MA', region: 'africa' },
  { name: 'Mozambique', code: 'MZ', region: 'africa' },
  { name: 'Nepal', code: 'NP', region: 'asia' },
  { name: 'Netherlands', code: 'NL', region: 'europe' },
  { name: 'New Zealand', code: 'NZ', region: 'oceania' },
  { name: 'Nigeria', code: 'NG', region: 'africa' },
  { name: 'Norway', code: 'NO', region: 'europe' },
  { name: 'Oman', code: 'OM', region: 'middle-east' },
  { name: 'Pakistan', code: 'PK', region: 'asia' },
  { name: 'Panama', code: 'PA', region: 'north-america' },
  { name: 'Papua New Guinea', code: 'PG', region: 'oceania' },
  { name: 'Paraguay', code: 'PY', region: 'south-america' },
  { name: 'Peru', code: 'PE', region: 'south-america' },
  { name: 'Philippines', code: 'PH', region: 'asia' },
  { name: 'Poland', code: 'PL', region: 'europe' },
  { name: 'Portugal', code: 'PT', region: 'europe' },
  { name: 'Qatar', code: 'QA', region: 'middle-east' },
  { name: 'Romania', code: 'RO', region: 'europe' },
  { name: 'Russia', code: 'RU', region: 'europe' },
  { name: 'Saudi Arabia', code: 'SA', region: 'middle-east' },
  { name: 'Senegal', code: 'SN', region: 'africa' },
  { name: 'Serbia', code: 'RS', region: 'europe' },
  { name: 'Singapore', code: 'SG', region: 'asia' },
  { name: 'Slovakia', code: 'SK', region: 'europe' },
  { name: 'Slovenia', code: 'SI', region: 'europe' },
  { name: 'South Africa', code: 'ZA', region: 'africa' },
  { name: 'South Korea', code: 'KR', region: 'asia' },
  { name: 'Spain', code: 'ES', region: 'europe' },
  { name: 'Sri Lanka', code: 'LK', region: 'asia' },
  { name: 'Sweden', code: 'SE', region: 'europe' },
  { name: 'Switzerland', code: 'CH', region: 'europe' },
  { name: 'Taiwan', code: 'TW', region: 'asia' },
  { name: 'Thailand', code: 'TH', region: 'asia' },
  { name: 'Trinidad and Tobago', code: 'TT', region: 'south-america' },
  { name: 'Tunisia', code: 'TN', region: 'africa' },
  { name: 'Turkey', code: 'TR', region: 'middle-east' },
  { name: 'Uganda', code: 'UG', region: 'africa' },
  { name: 'Ukraine', code: 'UA', region: 'europe' },
  { name: 'United Arab Emirates', code: 'AE', region: 'middle-east' },
  { name: 'United Kingdom', code: 'GB', region: 'europe' },
  { name: 'United States', code: 'US', region: 'north-america' },
  { name: 'United States Minor Outlying Islands', code: 'UM', region: 'north-america' },
  { name: 'Uruguay', code: 'UY', region: 'south-america' },
  { name: 'Venezuela', code: 'VE', region: 'south-america' },
  { name: 'Vietnam', code: 'VN', region: 'asia' },
  { name: 'Zimbabwe', code: 'ZW', region: 'africa' }
] as const;

export const COUNTRIES: CountryOption[] = [...countriesData]
  .sort((a, b) => a.name.localeCompare(b.name))
  .map((country) => ({
    name: country.name,
    code: country.code,
    iso3: toIso3(country.code, country.name),
    region: country.region as GeoRegion
  }));

export const COUNTRIES_BY_REGION: Record<GeoRegion, CountryOption[]> = (() => {
  const map: Record<GeoRegion, CountryOption[]> = {
    'global': [],
    'africa': [],
    'asia': [],
    'europe': [],
    'north-america': [],
    'south-america': [],
    'oceania': [],
    'middle-east': []
  };

  for (const country of COUNTRIES) {
    map[country.region].push(country);
  }

  map.global = [...COUNTRIES];

  return map;
})();
